import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { APP_CONFIG } from "../config/app";
import { Layout, Menu, Button, Divider, Drawer, Grid, Spin, theme } from "antd";
import type { MenuProps } from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  FolderOutlined,
} from "@ant-design/icons";
import { useMenu, useLogout } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useMobileMenu } from "../contexts/MobileContext";
import { ENV } from "../config/env";
import { useMenuFromApi } from "../hooks/useMenuFromApi";

import {
  transformMenuToSidebar,
  type SidebarMenuItem,
} from "../utils/menuTransformer";

const { Sider } = Layout;
const { useBreakpoint } = Grid;

const DEFAULT_SIDER_WIDTH = 230;
const MIN_SIDER_WIDTH = 180;
const MAX_SIDER_WIDTH = 400;
const SIDER_COLLAPSED_WIDTH = 80;
const DRAWER_WIDTH = 280;
const SIDER_WIDTH_STORAGE_KEY = "sider-width";

function useResizableSider() {
  const [width, setWidth] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDER_WIDTH_STORAGE_KEY);
      if (stored) {
        const parsed = Number(stored);
        if (parsed >= MIN_SIDER_WIDTH && parsed <= MAX_SIDER_WIDTH)
          return parsed;
      }
    } catch {
      // ignore
    }
    return DEFAULT_SIDER_WIDTH;
  });
  const [isDragging, setIsDragging] = useState(false);
  const widthRef = useRef(width);
  const siderRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef(0);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const siderLeft = siderRef.current?.getBoundingClientRect().left ?? 0;
    const newWidth = Math.min(
      MAX_SIDER_WIDTH,
      Math.max(MIN_SIDER_WIDTH, e.clientX - siderLeft),
    );
    widthRef.current = newWidth;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setWidth(newWidth));
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    try {
      localStorage.setItem(SIDER_WIDTH_STORAGE_KEY, String(widthRef.current));
    } catch {
      // ignore
    }
  }, [handleMouseMove]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      setIsDragging(true);
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [handleMouseMove, handleMouseUp],
  );

  const resetWidth = useCallback(() => {
    setWidth(DEFAULT_SIDER_WIDTH);
    widthRef.current = DEFAULT_SIDER_WIDTH;
    try {
      localStorage.setItem(
        SIDER_WIDTH_STORAGE_KEY,
        String(DEFAULT_SIDER_WIDTH),
      );
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      cancelAnimationFrame(rafRef.current);
    };
  }, [handleMouseMove, handleMouseUp]);

  return {
    siderWidth: width,
    isDragging,
    handleMouseDown,
    resetWidth,
    siderRef,
  };
}

export const CustomSider: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { t, i18n } = useTranslation();
  const { menuItems: refineMenuItems } = useMenu();
  const { mutate: logout } = useLogout();
  const location = useLocation();
  const screens = useBreakpoint();
  const { mobileMenuOpen, closeMobileMenu } = useMobileMenu();
  const { token } = theme.useToken();
  const {
    siderWidth: customWidth,
    isDragging,
    handleMouseDown,
    resetWidth,
    siderRef,
  } = useResizableSider();

  // Fetch menu from API
  const {
    menuItems: apiMenuItems,
    isLoading: menuLoading,
    isFetched: menuFetched,
    isError: menuError,
  } = useMenuFromApi();

  const isMobile = !screens.md;
  const siderWidth = collapsed ? SIDER_COLLAPSED_WIDTH : customWidth;

  // Transform API menu items to sidebar format
  const apiSidebarItems = useMemo(() => {
    if (!apiMenuItems || apiMenuItems.length === 0) {
      return null;
    }
    return transformMenuToSidebar(apiMenuItems, i18n.language);
  }, [apiMenuItems, i18n.language]);

  // Use API menu when fetched successfully (even if empty).
  // Only fallback to Refine menu when API call failed or hasn't been made yet.
  const useApiMenu = menuFetched && !menuError;

  // Find selected key based on current route
  const selectedKey = useMemo(() => {
    // Always check system items first (dashboard, inbasket)
    const systemMatch = refineMenuItems.find(
      (item) =>
        (item.meta as { group?: string })?.group === "system" &&
        item.route &&
        location.pathname.startsWith(item.route),
    );
    if (systemMatch) {
      return systemMatch.key?.toString();
    }

    if (useApiMenu && apiSidebarItems) {
      // Find in API menu items (recursive)
      const findKey = (items: SidebarMenuItem[]): string | undefined => {
        for (const item of items) {
          if (item.route && location.pathname.startsWith(item.route)) {
            return item.key;
          }
          if (item.children) {
            const childKey = findKey(item.children);
            if (childKey) return childKey;
          }
        }
        return undefined;
      };
      return findKey(apiSidebarItems);
    }
    // Fallback to Refine menu
    return refineMenuItems
      .find((item) => location.pathname.startsWith(item.route || ""))
      ?.key?.toString();
  }, [useApiMenu, apiSidebarItems, refineMenuItems, location.pathname]);

  // Find open keys for SubMenu (parent categories)
  const openKeys = useMemo(() => {
    if (!useApiMenu || !apiSidebarItems) return [];
    const keys: string[] = [];
    const findParentKeys = (
      items: SidebarMenuItem[],
      targetKey: string | undefined,
      parentKeys: string[] = [],
    ): boolean => {
      for (const item of items) {
        if (item.key === targetKey) {
          keys.push(...parentKeys);
          return true;
        }
        if (item.children) {
          if (
            findParentKeys(item.children, targetKey, [...parentKeys, item.key])
          ) {
            return true;
          }
        }
      }
      return false;
    };
    if (selectedKey) {
      findParentKeys(apiSidebarItems, selectedKey);
    }
    return keys;
  }, [useApiMenu, apiSidebarItems, selectedKey]);

  // Group Refine items by their meta.group property (fallback)
  const { systemItems, crmItems } = useMemo(() => {
    const system: typeof refineMenuItems = [];
    const crm: typeof refineMenuItems = [];

    refineMenuItems.forEach((item) => {
      const group = (item.meta as { group?: string })?.group;
      if (group === "system") {
        system.push(item);
      } else {
        crm.push(item);
      }
    });

    const sortByOrder = (
      a: (typeof refineMenuItems)[0],
      b: (typeof refineMenuItems)[0],
    ) => {
      const orderA = (a.meta as { order?: number })?.order ?? 999;
      const orderB = (b.meta as { order?: number })?.order ?? 999;
      return orderA - orderB;
    };

    // Apply VITE_VISIBLE_MENU_ITEMS whitelist to Refine fallback
    const visibleSet = new Set(
      ENV.VISIBLE_MENU_ITEMS.map((s) => s.toLowerCase()),
    );
    const showAll = visibleSet.size === 0 || visibleSet.has("*");
    const visibleCrm = showAll
      ? crm
      : crm.filter((item) => {
          const arasType = (item.meta as { arasType?: string })?.arasType;
          return arasType && visibleSet.has(arasType.toLowerCase());
        });

    return {
      systemItems: system.sort(sortByOrder),
      crmItems: visibleCrm.sort(sortByOrder),
    };
  }, [refineMenuItems]);

  // Get sidebar label for Refine menu items
  const getSidebarLabel = (item: (typeof refineMenuItems)[0]) => {
    const itemTypeKey = (item.meta as { itemTypeKey?: string })?.itemTypeKey;
    if (itemTypeKey) {
      const sidebarKey = `navigation.sidebar.${itemTypeKey}`;
      const translated = t(sidebarKey);
      if (translated !== sidebarKey) {
        return translated;
      }
    }
    return item.label;
  };

  // Handle navigation and close drawer on mobile
  const handleNavigation = () => {
    if (isMobile) {
      closeMobileMenu();
    }
  };

  // Convert SidebarMenuItem to Ant Design MenuProps items (recursive)
  const convertToAntdMenuItems = (
    items: SidebarMenuItem[],
  ): MenuProps["items"] => {
    return items.map((item) => {
      // If item has children, render as SubMenu
      if (item.children && item.children.length > 0) {
        return {
          key: item.key,
          icon: item.icon || <FolderOutlined />,
          label: item.label,
          children: convertToAntdMenuItems(item.children),
        };
      }

      // Leaf item - render with Link
      return {
        key: item.key,
        icon: item.icon,
        label: item.route ? (
          <Link to={item.route} onClick={handleNavigation}>
            {item.label}
          </Link>
        ) : (
          item.label
        ),
      };
    });
  };

  // Build system menu items (always shown at top)
  const systemMenuItems: MenuProps["items"] = useMemo(() => {
    return systemItems.map((item) => ({
      key: item.key,
      icon: item.icon,
      label: (
        <Link to={item.route || "/"} onClick={handleNavigation}>
          {item.label}
        </Link>
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemItems, isMobile]);

  // Build dynamic menu items (from API or fallback to Refine CRM)
  const dynamicMenuItems: MenuProps["items"] = useMemo(() => {
    // API fetched successfully - use API menu (even if empty, no fallback)
    if (useApiMenu) {
      if (apiSidebarItems && apiSidebarItems.length > 0) {
        return convertToAntdMenuItems(apiSidebarItems) || [];
      }
      return [];
    }

    // Fallback to Refine CRM menu only when API failed or not yet fetched
    return crmItems.map((item) => ({
      key: item.key,
      icon: item.icon,
      label: (
        <Link to={item.route || "/"} onClick={handleNavigation}>
          {getSidebarLabel(item)}
        </Link>
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useApiMenu, apiSidebarItems, crmItems, isMobile]);

  // Check if we should show divider between system and dynamic items
  const showDivider = useMemo(() => {
    if (menuLoading) return true; // Show divider while loading
    if (dynamicMenuItems && dynamicMenuItems.length > 0) {
      return systemItems.length > 0;
    }
    return false;
  }, [menuLoading, systemItems, dynamicMenuItems]);

  const logoutMenuItems: MenuProps["items"] = [
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("auth.logout"),
      onClick: () => {
        if (isMobile) {
          closeMobileMenu();
        }
        logout();
      },
      danger: true,
    },
  ];

  // Shared menu content for both mobile drawer and desktop sider
  const menuContent = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Header with title and collapse button - Position 1 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile || !collapsed ? "space-between" : "center",
          padding: isMobile || !collapsed ? "16px" : "16px 8px",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          minHeight: "64px",
          overflow: "hidden",
        }}
      >
        {(isMobile || !collapsed) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              marginRight: "8px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <img
              src={APP_CONFIG.appLogo}
              alt="Logo"
              style={{
                height: "40px",
                width: "auto",
                marginRight: "8px",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontWeight: "bold",
                fontSize: "16px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {APP_CONFIG.appName}
            </span>
          </div>
        )}
        {!isMobile && (
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: "16px",
              width: 32,
              height: 32,
              flexShrink: 0,
            }}
          />
        )}
      </div>

      {/* Navigation menu */}
      <div
        data-tour="sidebar-menu"
        style={{ flex: 1, overflow: "auto" }}
        className={`custom-sider-menu ${
          isMobile || !collapsed ? "expanded" : "collapsed"
        }`}
      >
        <style>
          {`
            .custom-sider-menu.expanded .ant-menu-item {
              height: auto !important;
              line-height: 1.5 !important;
              padding-top: 8px !important;
              padding-bottom: 8px !important;
            }
            .custom-sider-menu.expanded .ant-menu-item .ant-menu-title-content {
              white-space: normal !important;
              word-break: break-word !important;
            }
          `}
        </style>
        {/* System menu items - always visible immediately */}
        <Menu
          mode="inline"
          inlineCollapsed={!isMobile && collapsed}
          selectedKeys={selectedKey ? [selectedKey] : []}
          items={systemMenuItems}
          style={{
            borderRight: "none",
          }}
        />
        {/* Divider between system and dynamic items */}
        {showDivider && (
          <Divider style={{ margin: "8px 16px", width: "auto", minWidth: 0 }} />
        )}
        {/* Dynamic menu items.
            Show the previously-known menu immediately even while a refresh is
            in flight \u2014 only fall back to a spinner when there is genuinely
            nothing to render yet (first-ever load on this device). */}
        {dynamicMenuItems && dynamicMenuItems.length > 0 ? (
          <Menu
            mode="inline"
            inlineCollapsed={!isMobile && collapsed}
            selectedKeys={selectedKey ? [selectedKey] : []}
            defaultOpenKeys={openKeys}
            items={dynamicMenuItems}
            style={{
              borderRight: "none",
            }}
          />
        ) : menuLoading ? (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <Spin size="small" />
          </div>
        ) : null}
      </div>

      {/* Bottom section with divider and logout - Position 2 */}
      <div style={{ flexShrink: 0, paddingBottom: "16px" }}>
        <Divider style={{ margin: "8px 16px", width: "auto", minWidth: 0 }} />
        <Menu
          mode="inline"
          selectable={false}
          items={logoutMenuItems}
          style={{
            borderRight: "none",
          }}
        />
      </div>
    </div>
  );

  // Mobile: Render as Drawer
  if (isMobile) {
    return (
      <Drawer
        placement="left"
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        width={DRAWER_WIDTH}
        styles={{
          body: { padding: 0 },
          header: { display: "none" },
        }}
        destroyOnClose={false}
      >
        {menuContent}
      </Drawer>
    );
  }

  // Desktop: Render as fixed Sider with spacer
  return (
    <>
      <style>
        {`
          .sider-resize-handle {
            background-color: transparent;
            transition: background-color 0.2s;
          }
          .sider-resize-handle:hover,
          .sider-resize-handle.dragging {
            background-color: var(--primary-color);
          }
        `}
      </style>
      <Sider
        ref={siderRef}
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={customWidth}
        collapsedWidth={SIDER_COLLAPSED_WIDTH}
        style={{
          overflow: "visible",
          height: "100vh",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          backgroundColor: token.colorBgContainer,
          borderRight: `1px solid ${token.colorBorderSecondary}`,
          zIndex: 999,
          transition: isDragging ? "none" : undefined,
        }}
      >
        <div style={{ height: "100%", overflow: "auto" }}>{menuContent}</div>

        {/* Drag handle for resizing — double-click to reset to default */}
        {!collapsed && (
          <div
            className={`sider-resize-handle${isDragging ? " dragging" : ""}`}
            onMouseDown={handleMouseDown}
            onDoubleClick={resetWidth}
            style={{
              position: "absolute",
              top: 0,
              right: -4,
              width: 8,
              height: "100%",
              cursor: "col-resize",
              zIndex: 1000,
            }}
          />
        )}
      </Sider>

      {/* Spacer to offset content area */}
      <div
        style={{
          width: siderWidth,
          minWidth: siderWidth,
          maxWidth: siderWidth,
          transition: isDragging ? "none" : "all 0.2s",
        }}
      />
    </>
  );
};
